import { useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isNavItemCurrent } from '../lib/navCurrent';
import { ChevronsUpDown, Bug, Sun, Moon, LogOut, Megaphone, Keyboard, User, Smartphone } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Metronome } from './icons/Metronome';
import { FinishSetupTile } from './setup/SetupCards';
import { CaptureMenu } from './CaptureMenu';
import { PLATFORM_NAV, TENANT_FOOTER, TENANT_NAV, visibleGroups, type NavEntry } from '../navigation/navConfig';

const ACCOUNT_ITEM_CLASS =
  'flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface SlimRailProps {
  isPlatformAdmin: boolean;
  themeMode: 'dark' | 'light';
  onToggleTheme: () => void;
  onReportBug: () => void;
  onShowShortcuts: () => void;
  onLogout: () => void;
  userInitial: string;
  userDisplayName: string;
  userRole: string;
  // Filter items by role and plan. AppLayout passes the same predicate it
  // uses for the mobile drawer (lib/planGates), so who sees what lives in
  // one place, and both render navigation/navConfig.
  isVisible: (path: string) => boolean;
  /** Things waiting for a yes in Review (null while unknown). */
  reviewCount?: number | null;
}

/**
 * The labelled sidebar at 220px (web app v4 plan §5.1): brand, + Capture, the
 * groups from navigation/navConfig, Settings and Organisation pinned at the
 * foot, then the account row, whose menu holds your profile, theme, help and
 * sign out.
 */
export function SlimRail(props: SlimRailProps) {
  const {
    isPlatformAdmin,
    themeMode,
    onToggleTheme,
    onReportBug,
    onShowShortcuts,
    onLogout,
    userInitial,
    userDisplayName,
    userRole,
    isVisible,
    reviewCount,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();

  const groups = useMemo(
    () => visibleGroups(isPlatformAdmin ? PLATFORM_NAV : TENANT_NAV, isVisible),
    [isPlatformAdmin, isVisible],
  );
  const footer = useMemo(
    () => (isPlatformAdmin ? [] : TENANT_FOOTER.filter(e => isVisible(e.path))),
    [isPlatformAdmin, isVisible],
  );

  const [accountOpen, setAccountOpen] = useState(false);
  const accountTriggerRef = useRef<HTMLButtonElement>(null);

  const ThemeIcon = themeMode === 'dark' ? Sun : Moon;
  const themeLabel = themeMode === 'dark' ? 'Switch to light' : 'Switch to dark';

  // Close the account menu before acting. The rail never unmounts, so an
  // uncontrolled menu stays open after navigating, and the support dialog
  // would open underneath it. Focus goes back to the row first, so a dialog
  // the action opens has somewhere to return it to — the menu item that was
  // clicked is about to unmount.
  const runAccountAction = (action: () => void) => {
    setAccountOpen(false);
    accountTriggerRef.current?.focus();
    action();
  };

  return (
    <div
      className={cn(
        'fixed inset-y-0 left-0 z-40 hidden w-[220px] flex-col overflow-hidden md:flex',
        // macOS-style sidebar glass in light. In dark the rail is solid
        // slate: a translucent fill over the dark page reads as grey haze,
        // and blurring a flat page costs GPU for no visible effect.
        'border-r border-white/40 dark:border-border',
        'bg-white/45 dark:bg-card',
        'backdrop-blur-2xl backdrop-saturate-150 dark:backdrop-filter-none',
        'shadow-[1px_0_2px_rgba(15,23,42,0.04),12px_0_40px_-20px_rgba(15,23,42,0.12),inset_0_1px_0_rgba(255,255,255,0.7)]',
        'dark:shadow-[1px_0_2px_rgba(0,0,0,0.4),12px_0_40px_-20px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.08)]',
        'before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-32',
        'before:bg-[linear-gradient(to_bottom,rgba(255,255,255,0.5)_0%,rgba(255,255,255,0.12)_50%,rgba(255,255,255,0)_100%)]',
        'after:pointer-events-none after:absolute after:inset-0 after:[mix-blend-mode:plus-lighter]',
        'after:bg-[linear-gradient(135deg,rgba(255,255,255,0.30)_0%,rgba(255,255,255,0.08)_28%,rgba(255,255,255,0)_60%)]',
        'dark:before:hidden dark:after:hidden',
        '[&>*]:relative [&>*]:z-10',
      )}
    >
      {/* Brand row — metronome + wordmark + by Stratium */}
      <button
        type="button"
        onClick={() => navigate(isPlatformAdmin ? '/platform' : '/')}
        className="flex w-full items-center gap-2.5 px-4 pb-3 pt-4 text-left hover:opacity-90"
        aria-label="Cadence home"
      >
        <Metronome size={26} className="block text-foreground" title="" />
        <div className="flex flex-col">
          <span className="text-[15px] font-bold leading-tight tracking-tight text-foreground">
            Cadence
          </span>
          <span className="text-[10px] font-medium uppercase leading-tight tracking-[0.08em] text-muted-foreground">
            by Stratium
          </span>
        </div>
      </button>

      {!isPlatformAdmin && (
        <div className="px-3 pb-2">
          <CaptureMenu />
        </div>
      )}

      {/* Grouped nav */}
      <nav aria-label="Primary" className="flex-1 overflow-y-auto px-2 pb-2">
        {groups.map(group => (
          <div key={group.id} className="mb-3">
            {/* Each list is named by its group, so a screen reader says "Work" before its entries. */}
            <p id={`rail-group-${group.id}`} className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {group.label}
            </p>
            <ul aria-labelledby={`rail-group-${group.id}`} className="flex flex-col gap-0.5">
              {group.entries.map(item => (
                <li key={item.path}>
                  <RailRow item={item} isActive={isNavItemCurrent(item.path, location.pathname)}
                    count={item.badge === 'review' ? reviewCount : null} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Pinned: the "Finish setup" tile while Claude steps are left, then
          Settings and Organisation (or Teams). */}
      {!isPlatformAdmin && (
        <div className="px-2 pb-2">
          <FinishSetupTile />
          {/* A landmark of its own, as the phone drawer's are inside its nav. */}
          <nav aria-label="Settings and organisation">
          <ul className="flex flex-col gap-0.5">
            {footer.map(item => (
              <li key={item.path}>
                <RailRow item={item} isActive={isNavItemCurrent(item.path, location.pathname)} />
              </li>
            ))}
          </ul>
          </nav>
        </div>
      )}

      {/* Pinned bottom — one labelled account row. Theme, support and
          preferences live in its menu so each has a visible label
          rather than a hover-only tooltip. */}
      <div className="border-t border-border/60 p-2 dark:border-border">
        <Popover open={accountOpen} onOpenChange={setAccountOpen}>
          <PopoverTrigger asChild>
            <button
              ref={accountTriggerRef}
              type="button"
              aria-label={`Account: ${userDisplayName}${userRole ? `, ${userRole}` : ''}`}
              className="flex w-full items-center gap-2.5 rounded-lg p-2 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-accent"
            >
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-[13px] font-semibold text-primary-foreground">
                {userInitial}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-semibold leading-tight text-foreground">
                  {userDisplayName}
                </span>
                {userRole && (
                  <span className="truncate text-xs leading-tight text-muted-foreground">{userRole}</span>
                )}
              </span>
              <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" aria-label="Account" className="w-[236px]">
            <div className="px-2 pb-2 pt-1">
              <p className="truncate text-sm font-semibold leading-tight">{userDisplayName}</p>
              {userRole && <p className="truncate text-xs text-muted-foreground">{userRole}</p>}
            </div>
            <div className="my-1 border-t border-border/60" />
            <button type="button" onClick={() => runAccountAction(() => navigate('/settings/profile'))} className={ACCOUNT_ITEM_CLASS}>
              <User className="h-4 w-4 text-foreground/70" />
              Profile
            </button>
            {!isPlatformAdmin && (
              <button
                type="button"
                onClick={() => runAccountAction(() => navigate('/settings/apps'))}
                className={ACCOUNT_ITEM_CLASS}
              >
                <Smartphone className="h-4 w-4 text-foreground/70" />
                Get the apps
              </button>
            )}
            <button type="button" onClick={() => runAccountAction(onToggleTheme)} className={ACCOUNT_ITEM_CLASS}>
              <ThemeIcon className="h-4 w-4 text-foreground/70" />
              {themeLabel}
            </button>
            <div className="my-1 border-t border-border/60" />
            <p className="px-2 pb-0.5 pt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Help & feedback</p>
            <button type="button" onClick={() => runAccountAction(onReportBug)} className={ACCOUNT_ITEM_CLASS}>
              <Bug className="h-4 w-4 text-foreground/70" />
              Report a problem
            </button>
            {!isPlatformAdmin && (
              <button type="button" onClick={() => runAccountAction(() => navigate('/feature-requests'))} className={ACCOUNT_ITEM_CLASS}>
                <Megaphone className="h-4 w-4 text-foreground/70" />
                Feature requests
              </button>
            )}
            <button type="button" onClick={() => runAccountAction(onShowShortcuts)} className={ACCOUNT_ITEM_CLASS}>
              <Keyboard className="h-4 w-4 text-foreground/70" />
              Keyboard shortcuts
            </button>
            <div className="my-1 border-t border-border/60" />
            <button
              type="button"
              onClick={() => runAccountAction(onLogout)}
              className={cn(ACCOUNT_ITEM_CLASS, 'text-destructive hover:bg-destructive/10')}
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

interface RailRowProps {
  item: NavEntry;
  isActive: boolean;
  /** A count beside the label (Review); none when null or 0. */
  count?: number | null;
}

export function RailRow({ item, isActive, count }: RailRowProps) {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'group relative flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive
          ? 'bg-primary/10 font-semibold text-primary'
          // Full-strength label so the nav reads clearly on the dark sidebar.
          : 'text-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {isActive && (
        <span
          aria-hidden
          className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary"
        />
      )}
      <Icon
        className={cn(
          'h-4 w-4 shrink-0',
          isActive ? 'text-primary' : 'text-foreground/70 group-hover:text-foreground',
        )}
      />
      <span className="truncate">{item.label}</span>
      {!!count && count > 0 && (
        <span className="ml-auto rounded-full bg-attention px-1.5 text-xs font-semibold leading-[18px] text-attention-foreground">
          {count > 99 ? '99+' : count}
          <span className="sr-only"> waiting</span>
        </span>
      )}
    </Link>
  );
}
